import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, tap, filter } from 'rxjs/operators';
import { LanguageActions } from './language.actions';
import { AuthActions } from '@app/store/auth/auth.actions';
import { Language } from '@app/shared/models';
import { LoggerService } from '@app/core/infrastructure';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class LanguageEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly logger = inject(LoggerService).scope('LanguageEffects');
  private readonly translateService = inject(TranslateService);

  private readonly STORAGE_KEY = 'active_language';

  initialize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.initialize),
      map(() => {
        const storedLanguage = localStorage.getItem(this.STORAGE_KEY);
        const language = (storedLanguage && Object.values(Language).includes(storedLanguage as Language))
          ? storedLanguage as Language
          : this.getBrowserLanguage();
        this.translateService.setDefaultLang('en');
        this.translateService.use(language.toLowerCase());
        this.logger.debug('Initialize language:', language);
        return LanguageActions.initializeSuccess({ language });
      }),
      catchError(error => {
        this.logger.error('Initialize failed:', error);
        return of(LanguageActions.initializeFailure({ error: error.message }));
      })
    )
  );

  setActiveLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.setActiveLanguage),
      tap(({ language }) => {
        localStorage.setItem(this.STORAGE_KEY, language);
        this.translateService.use(language.toLowerCase());
        this.logger.debug('Active language set:', language);
      }),
      map(({ language }) => LanguageActions.setActiveLanguageSuccess({ language })),
      catchError(error => {
        this.logger.error('Set active language failed:', error);
        return of(LanguageActions.setActiveLanguageFailure({ error: error.message }));
      })
    )
  );

  updateUserPreferredLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.updateUserPreferredLanguage),
      tap(({ language }) => {
        localStorage.setItem(this.STORAGE_KEY, language);
        this.translateService.use(language.toLowerCase());
        this.logger.debug('User preferred language updated:', language);
      }),
      map(({ language }) => LanguageActions.updateUserPreferredLanguageSuccess({ language })),
      catchError(error => {
        this.logger.warn('Update user preferred language failed:', error);
        return of(LanguageActions.updateUserPreferredLanguageFailure({ error: error.message }));
      })
    )
  );

  resetLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.resetLanguage),
      tap(() => {
        localStorage.removeItem(this.STORAGE_KEY);
        this.translateService.use('en');
        this.logger.debug('Language reset to default');
      })
    ),
    { dispatch: false }
  );

  private getBrowserLanguage(): Language {
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    const supportedLanguages = Object.values(Language);
    return supportedLanguages.includes(browserLang as Language)
      ? (browserLang as Language)
      : Language.EN;
  }

  ngrxOnInitEffects(): Action {
    return LanguageActions.initialize();
  }
}
